import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Archive, Lock, Unlock, Calendar, Plus } from "lucide-react";
import { toast } from "sonner";
import { BACKEND_URL, API } from "@/lib/api";


export default function MemoryBox({ eventId, isOwner }) {
  const [memories, setMemories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    title: "",
    message: "",
    author_name: "",
    open_date: ""
  });

  useEffect(() => {
    fetchMemories();
  }, [eventId]);

  const fetchMemories = async () => {
    try {
      const response = await axios.get(`${API}/events/${eventId}/memory-box`);
      setMemories(response.data);
    } catch (error) {
      console.error("Failed to fetch memories:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.message || !formData.author_name) {
      toast.error("Alle Felder außer Öffnungsdatum sind erforderlich!");
      return;
    }

    try {
      await axios.post(
        `${API}/events/${eventId}/memory-box`,
        formData
      );
      
      toast.success("Erinnerung gespeichert!");
      setFormData({ title: "", message: "", author_name: "", open_date: "" });
      fetchMemories();
    } catch (error) {
      console.error("Failed to add memory:", error);
      toast.error("Fehler beim Hinzufügen");
    }
  };

  if (loading) {
    return (
      <Card className="p-6">
        <div className="flex items-center justify-center">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Card>
    );
  }

  const canOpen = (memory) => {
    if (!memory.open_date) return true;
    return new Date(memory.open_date) <= new Date();
  };

  const lockedMemories = memories.filter(m => !canOpen(m));
  const unlockedMemories = memories.filter(m => canOpen(m));

  return (
    <Card className="p-6">
      <div className="mb-6">
        <h3 className="text-lg font-semibold flex items-center gap-2 mb-2">
          <Archive className="w-5 h-5 text-primary" />
          Erinnerungs-Box (Zeitkapsel)
        </h3>
        <p className="text-sm text-muted-foreground">
          Speichert Erinnerungen und Nachrichten für die Zukunft
        </p>
      </div>

      {/* Add Memory Form */}
      <form onSubmit={handleSubmit} className="mb-6 p-4 border rounded-lg space-y-4 bg-secondary/20">
        <div>
          <Label>Titel *</Label>
          <Input
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            placeholder="z.B. Brief an mein zukünftiges Ich"
            required
          />
        </div>

        <div>
          <Label>Nachricht/Erinnerung *</Label>
          <Textarea
            value={formData.message}
            onChange={(e) => setFormData({ ...formData, message: e.target.value })}
            placeholder="Was möchtest du festhalten?"
            rows={4}
            required
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Dein Name *</Label>
            <Input
              value={formData.author_name}
              onChange={(e) => setFormData({ ...formData, author_name: e.target.value })}
              placeholder="Name"
              required
            />
          </div>
          <div>
            <Label className="flex items-center gap-2">
              <Lock className="w-3 h-3" />
              Öffnen ab (optional)
            </Label>
            <Input
              type="date"
              value={formData.open_date}
              onChange={(e) => setFormData({ ...formData, open_date: e.target.value })}
            />
          </div>
        </div>

        <p className="text-xs text-muted-foreground">
          💡 Tipp: Setze ein Datum in der Zukunft, um die Erinnerung erst später zu enthüllen!
        </p>

        <Button type="submit" className="w-full">
          <Plus className="w-4 h-4 mr-2" />
          Erinnerung speichern
        </Button>
      </form>

      {/* Unlocked Memories */}
      {unlockedMemories.length > 0 && (
        <div className="mb-6">
          <h4 className="font-medium text-sm mb-3 flex items-center gap-2">
            <Unlock className="w-4 h-4 text-green-600" />
            Enthüllte Erinnerungen ({unlockedMemories.length})
          </h4>
          <div className="space-y-3">
            {unlockedMemories.map((memory) => (
              <div key={memory.id} className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <h5 className="font-medium">{memory.title}</h5>
                  <Unlock className="w-4 h-4 text-green-600 flex-shrink-0" />
                </div>
                <p className="text-sm text-foreground whitespace-pre-wrap mb-2">{memory.message}</p>
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>von {memory.author_name}</span>
                  <span>Erstellt: {new Date(memory.created_at).toLocaleDateString('de-DE')}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Locked Memories */}
      {lockedMemories.length > 0 && (
        <div>
          <h4 className="font-medium text-sm mb-3 flex items-center gap-2">
            <Lock className="w-4 h-4 text-amber-600" />
            Versiegelte Erinnerungen ({lockedMemories.length})
          </h4>
          <div className="space-y-3">
            {lockedMemories.map((memory) => (
              <div key={memory.id} className="p-4 bg-amber-50 border border-amber-200 rounded-lg relative overflow-hidden">
                <div className="blur-sm select-none">
                  <h5 className="font-medium mb-2">{memory.title}</h5>
                  <p className="text-sm text-muted-foreground">████████████████</p>
                </div>
                <div className="absolute inset-0 flex items-center justify-center bg-amber-50/80 backdrop-blur-sm">
                  <div className="text-center">
                    <Lock className="w-8 h-8 text-amber-600 mx-auto mb-2" />
                    <p className="text-sm font-medium text-amber-700">
                      Öffnet am {new Date(memory.open_date).toLocaleDateString('de-DE')}
                    </p>
                    <p className="text-xs text-amber-600 mt-1">von {memory.author_name}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {memories.length === 0 && (
        <div className="text-center py-8 text-muted-foreground">
          <Archive className="w-12 h-12 mx-auto mb-2 opacity-30" />
          <p>Die Erinnerungs-Box ist noch leer</p>
          <p className="text-xs mt-1">Erstelle deine erste Zeitkapsel!</p>
        </div>
      )}
    </Card>
  );
}